"use client";

import React, { useState, useEffect } from "react";
import { Menu, ChevronDown, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function TopBar() {
  return <div className="w-full border-b border-white/10 bg-secondary">
      <div className="container mx-auto px-6 md:px-12 h-12 flex items-center justify-between bg-secondary">
        
        <Link to="tel:2408991278" className="flex items-center gap-2 !text-white hover:!text-secondary-500 transition-colors duration-300 font-default font-semibold text-sm">
          <Phone className="size-4 text-secondary-500" />
          <span>(240) 899-1278</span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/AboutPage" className="!text-white uppercase tracking-wide transition-colors duration-300 !text-xs hover:!text-secondary-500">
            About Us
          </Link>

          <Link to="/BlogPage" className="!text-white uppercase tracking-wide transition-colors duration-300 !text-xs hover:!text-secondary-500">
            Blog
          </Link>

          <Link to="#" className="!text-white uppercase tracking-wide transition-colors duration-300 !text-xs hover:!text-secondary-500">FAQs</Link>
        {}

        </div>

      </div>
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems
  } = useMenu();
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        return <NavigationMenuItem key={item.id} data-index={i}>
              {hasChildren ? <>
                  <NavigationMenuTrigger className="bg-transparent p-0 h-auto font-default text-[14px] font-medium tracking-wide uppercase text-foreground hover:text-secondary-500 transition-colors duration-300 focus:bg-transparent data-[state=open]:bg-transparent hover:bg-transparent">
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-primary-900 border-none min-w-[200px] z-[100] shadow-xl">
                    <ul className="flex flex-col p-4 gap-2">
                      {item.children?.map((child, childIdx) => <li key={child.id} data-index={childIdx}>
                          <NavigationMenuLink asChild>
                            <Link to={child.href || "#"} className="block font-default text-[13px] font-medium tracking-wide uppercase text-white hover:text-secondary-500 transition-colors duration-300 py-2">
                              {child.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="font-default text-[14px] font-medium tracking-wide uppercase text-foreground hover:text-secondary-500 transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-secondary-500 hover:after:w-full after:transition-all after:duration-300">
                    {item.label}
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const toggleSubmenu = (id: string) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col items-center gap-8 mt-12">
        <div className="h-8 w-32 bg-muted/50 animate-pulse" />
        <div className="h-8 w-32 bg-muted/50 animate-pulse" />
        <div className="h-8 w-32 bg-muted/50 animate-pulse" />
      </div>;
  }
  return <div className="flex flex-col items-center justify-center gap-6 mt-10">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = openSubmenus[item.id];
      return <div key={item.id} data-index={i} className="w-full flex flex-col items-center">
            <div className="flex items-center gap-2">
              <Link to={item.href || "#"} onClick={onNavigate} className="font-default text-xl font-bold tracking-wider uppercase text-foreground hover:text-secondary-500 transition-colors duration-300">
                {item.label}
              </Link>
              {hasChildren && <Button variant="ghost" size="icon" onClick={() => toggleSubmenu(item.id)} className="text-foreground hover:text-secondary-500 hover:bg-transparent" aria-label="Toggle submenu">
                  <ChevronDown className={cn("size-6 transition-transform duration-300", isOpen && "rotate-180")} />
                </Button>}
            </div>
            
            {hasChildren && isOpen && <div className="w-full mt-4 py-4 pl-4 border-l-2 border-secondary flex flex-col items-center gap-5">
                {item.children?.map((child, childIdx) => <Link key={child.id} data-index={childIdx} to={child.href || "#"} onClick={onNavigate} className="font-default text-base sm:text-lg font-medium tracking-wide uppercase text-foreground hover:text-secondary-500 active:text-secondary-500 transition-colors duration-300 py-2">
                    {child.label}
                  </Link>)}
              </div>}
          </div>;
    })}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="232424" id="header" className={`dark relative w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border shadow-lg z-40" : "bg-background border-b border-transparent z-30"}`}>
        <TopBar />
      <div className="container mx-auto px-6 md:px-12 h-24 flex items-center justify-between relative">
        
        <div className="w-1/3 hidden md:flex items-center gap-6">
          <Link to="/" className="block group">
            <img src="https://liberal-koi.10web.cloud/wp-content/uploads/2026/05/LOGO-2.png" alt="Eagle Exteriors" className="h-12 w-auto object-contain" loading="lazy" />
          </Link>
          
        </div>

        
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:block">
          
        </div>

        
        <div className="w-full md:w-2/3 flex justify-end items-center gap-8">
      
          <div className="hidden md:flex">
            <MenuProvider menu_id="43">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>
          <Button className="hidden lg:flex bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-semibold uppercase tracking-wider px-6 py-6 transition-transform hover:scale-[1.02]" asChild>
            <Link to="/ContactPage">Get a Free Estimate</Link>
          </Button>
        </div>

        
        <div className="md:hidden ml-auto z-10">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-primary hover:bg-transparent hover:text-primary/80">
                <Menu className="size-8" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="h-[100dvh] w-full bg-background/95 backdrop-blur-xl border-none pt-24 px-6 overflow-y-auto">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="absolute top-8 left-1/2 -translate-x-1/2">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <img src="https://liberal-koi.10web.cloud/wp-content/uploads/2026/05/LOGO-2.png" alt="Eagle Exteriors" className="h-12 w-auto object-contain" loading="lazy" />
                </Link>
              </div>
              <MenuProvider menu_id="43">
                <MobileMenuConsumer onNavigate={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
              <div className="mt-12 flex flex-col gap-4 justify-center pb-12">
                <Button className="bg-secondary text-secondary-foreground rounded-lg font-default font-bold uppercase tracking-widest w-full py-8 text-lg shadow-lg hover:scale-[1.02] transition-transform" asChild>
                  <Link to="tel:2408991278" onClick={() => setIsMobileMenuOpen(false)}>Call Now</Link>
                </Button>
                <Button className="bg-primary text-primary-foreground rounded-lg font-default font-bold uppercase tracking-widest w-full py-8 text-lg shadow-lg hover:scale-[1.02] transition-transform" asChild>
                  <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)}>Book Free Estimate</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}